import { createContext, useCallback, useContext, useEffect, useState } from 'react';

// Minimal toast system. Used for background event notifications (agent claims,
// dispatches, build status) and for optimistic action feedback ("task
// dispatched to forge-01"). Dismissed after a timeout or on click.

export type ToastKind = 'info' | 'success' | 'warn' | 'error';

export interface Toast {
  id: string;
  kind: ToastKind;
  title: string;
  body?: string;
  ttlMs?: number;
  createdAt: number;
}

interface ToastState {
  toasts: Toast[];
  push: (t: Omit<Toast, 'id' | 'createdAt'>) => void;
  dismiss: (id: string) => void;
}

const ToastContext = createContext<ToastState | null>(null);

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const push = useCallback((t: Omit<Toast, 'id' | 'createdAt'>) => {
    const toast: Toast = {
      ...t,
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      createdAt: Date.now(),
    };
    setToasts((prev) => [...prev, toast].slice(-5));
  }, []);

  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  // Auto-dismiss expired toasts
  useEffect(() => {
    if (toasts.length === 0) return;
    const timers = toasts.map((t) => {
      const ttl = t.ttlMs ?? 5000;
      const elapsed = Date.now() - t.createdAt;
      const remaining = Math.max(0, ttl - elapsed);
      return setTimeout(() => dismiss(t.id), remaining);
    });
    return () => timers.forEach(clearTimeout);
  }, [toasts, dismiss]);

  return (
    <ToastContext.Provider value={{ toasts, push, dismiss }}>
      {children}
      <ToastContainer toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
}

export function useToast(): ToastState {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside ToastProvider');
  return ctx;
}

function ToastContainer({
  toasts,
  onDismiss,
}: {
  toasts: Toast[];
  onDismiss: (id: string) => void;
}) {
  return (
    <div className="fixed bottom-4 right-4 z-[100] flex flex-col gap-2 pointer-events-none">
      {toasts.map((t) => (
        <div
          key={t.id}
          onClick={() => onDismiss(t.id)}
          className={
            'pointer-events-auto panel min-w-[260px] max-w-sm p-3 cursor-pointer animate-in fade-in slide-in-from-right-2 ' +
            (t.kind === 'success'
              ? 'border-green/60 bg-green/10'
              : t.kind === 'error'
                ? 'border-red/60 bg-red/10'
                : t.kind === 'warn'
                  ? 'border-amber/60 bg-amber/10'
                  : 'border-border')
          }
        >
          <div className="flex items-start gap-2">
            <span
              className={
                'mt-1 status-dot shrink-0 ' +
                (t.kind === 'success'
                  ? 'status-ok'
                  : t.kind === 'error'
                    ? 'status-down'
                    : t.kind === 'warn'
                      ? 'status-degraded'
                      : 'bg-dim')
              }
            />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold">{t.title}</div>
              {t.body && (
                <div className="text-xs text-muted font-mono mt-0.5">{t.body}</div>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
